import { Alert, Button, Divider, Group, Paper, Stack, Switch, Text, Title } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useBrandsStore } from "../Brands/brandsStore";
import { notifications } from "@mantine/notifications";

const PRESETS = {
  semi: { enabled: true, requireApprovalBeforeGeneration: false, requireApprovalBeforeTranslation: false, requireApprovalBeforePublish: true },
  full: { enabled: true, requireApprovalBeforeGeneration: false, requireApprovalBeforeTranslation: false, requireApprovalBeforePublish: false },
  manual: { enabled: false, requireApprovalBeforeGeneration: true, requireApprovalBeforeTranslation: true, requireApprovalBeforePublish: true },
};

export default function AutopilotSettings({ brand }) {
  const { updateBrand } = useBrandsStore();

  const autopilot = brand.autopilot || {
    enabled: false,
    requireApprovalBeforeGeneration: true,
    requireApprovalBeforeTranslation: true,
    requireApprovalBeforePublish: true,
  };

  const save = async (updated) => {
    try {
      await updateBrand(brand.id, { ...brand, autopilot: updated });
    } catch {
      notifications.show({ title: "Error", message: "Failed to save autopilot settings", color: "red" });
    }
  };

  const toggle = (field) => {
    save({ ...autopilot, [field]: !autopilot[field] });
  };

  const applyPreset = (key) => save(PRESETS[key]);

  return (
    <Stack mt="md" gap="lg">
      <div>
        <Title order={3}>Content Autopilot</Title>
        <Text size="sm" c="dimmed" mt={4}>
          Automate your content pipeline. When enabled, newly created content plans
          will auto-approve workstreams and progress through generation, translation,
          and publishing based on the gates below.
        </Text>
      </div>

      <Paper p="md" withBorder>
        <Switch
          label="Enable Autopilot"
          description="Turn on automated content pipeline for this brand"
          checked={autopilot.enabled}
          onChange={() => {
            if (autopilot.enabled) {
              // Turning off — reset to manual
              applyPreset("manual");
            } else {
              // Turning on — default to semi-auto
              applyPreset("semi");
            }
          }}
          size="md"
        />
      </Paper>

      {autopilot.enabled && (
        <>
          <Divider label="Automation Gates" />

          <Paper p="md" withBorder>
            <Stack gap="md">
              <Switch
                label="Auto-generate content"
                description="Content generation starts immediately when workstreams are approved, without waiting for manual approval per item"
                checked={!autopilot.requireApprovalBeforeGeneration}
                onChange={() => toggle("requireApprovalBeforeGeneration")}
              />

              <Switch
                label="Auto-translate"
                description="Automatically translate to all target languages after content is generated"
                checked={!autopilot.requireApprovalBeforeTranslation}
                onChange={() => toggle("requireApprovalBeforeTranslation")}
              />

              <Switch
                label="Auto-publish"
                description="Publish content on its scheduled date without manual review"
                checked={!autopilot.requireApprovalBeforePublish}
                onChange={() => toggle("requireApprovalBeforePublish")}
              />

              {!autopilot.requireApprovalBeforePublish && (
                <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
                  <Text size="sm">
                    Content will be published automatically on schedule. You'll receive an
                    email notification 24 hours before each scheduled publication.
                    Full auto skips human review.
                  </Text>
                </Alert>
              )}
            </Stack>
          </Paper>

          <Divider label="Presets" />

          <Group>
            <Button
              variant={!autopilot.requireApprovalBeforeGeneration && !autopilot.requireApprovalBeforeTranslation && autopilot.requireApprovalBeforePublish ? "filled" : "light"}
              size="xs"
              onClick={() => applyPreset("semi")}
            >
              Semi-auto
            </Button>
            <Button
              variant={!autopilot.requireApprovalBeforeGeneration && !autopilot.requireApprovalBeforeTranslation && !autopilot.requireApprovalBeforePublish ? "filled" : "light"}
              size="xs"
              color="green"
              onClick={() => applyPreset("full")}
            >
              Full auto
            </Button>
            <Button
              variant="light"
              size="xs"
              color="gray"
              onClick={() => applyPreset("manual")}
            >
              All manual
            </Button>
          </Group>

          <Text size="xs" c="dimmed">
            Semi-auto: generate and translate automatically, pause before publishing for review.
            Full auto: entire pipeline runs hands-free and skips human review. All manual: disables autopilot.
          </Text>
        </>
      )}
    </Stack>
  );
}
